import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { HERO_MEDIA, SIGNALS } from "../../constants/verify";
import Images from "../../utils/Images";
import { ArrowIcon, BrandMark, EASE, MediaSlot, useFade } from "./ui";

function VerifyHero() {
  const { reduce } = useFade();
  const [active, setActive] = useState(0);
  const fadeUp = {
    hidden: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: reduce ? { duration: 0 } : { duration: 0.55, ease: EASE },
    },
  };

  useEffect(() => {
    if (reduce) return undefined;
    const id = window.setInterval(() => {
      setActive((step) => (step + 1) % SIGNALS.length);
    }, 3400);
    return () => window.clearInterval(id);
  }, [reduce]);

  const current = SIGNALS[active];

  return (
    <section
      id="simverify-hero"
      data-tone="ice"
      className="relative flex min-h-[100svh] flex-col overflow-hidden bg-[#eef3fb] text-navy"
      aria-labelledby="simverify-heading"
    >
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(60% 50% at 85% 20%, rgba(34,211,238,0.18), transparent 55%), radial-gradient(50% 40% at 10% 80%, rgba(255,191,24,0.14), transparent 50%)",
        }}
      />

      <div className="relative z-[2] flex min-h-[100svh] flex-1 flex-col pt-[5.5rem]">
        <div className="border-y border-navy/10 bg-white/70 backdrop-blur-sm">
          <div className="mx-auto grid max-w-8xl grid-cols-2 lg:grid-cols-4">
            {SIGNALS.map((item, index) => {
              const on = index === active;
              return (
                <button
                  key={item.n}
                  type="button"
                  onClick={() => setActive(index)}
                  onMouseEnter={() => setActive(index)}
                  className={`relative flex min-h-[4.5rem] cursor-pointer flex-col justify-center gap-1 border-navy/10 px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-verify-gold sm:px-6 ${
                    index > 0 ? "border-l" : ""
                  } ${on ? "bg-white" : "hover:bg-white/80"}`}
                  aria-current={on ? "true" : undefined}
                >
                  {on ? (
                    <span className="absolute inset-x-4 bottom-0 h-[3px] bg-verify-royal sm:inset-x-6" aria-hidden="true" />
                  ) : null}
                  <span className={`font-mono text-[10px] tracking-[0.18em] ${on ? "text-verify-royal" : "text-navy/35"}`}>
                    {item.n}
                  </span>
                  <span className="text-sm font-semibold tracking-tight text-navy">{item.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="relative mx-auto grid w-full max-w-8xl flex-1 items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-14 lg:px-8 lg:py-16">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <BrandMark
              src={Images.simverify_logo}
              alt="SimVerify"
              className="h-9 w-auto max-w-[13rem] object-contain object-left sm:h-10"
            />
            <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.32em] text-navy/40">
              Africa&apos;s intelligent digital trust platform
            </p>
            <h1
              id="simverify-heading"
              className="mt-4 font-semibold leading-[1.05] tracking-tight text-navy [font-size:clamp(2.15rem,6vw,4.35rem)]"
            >
              Verify with confidence.
              <span className="mt-1 block text-verify-royal">Decide with intelligence.</span>
            </h1>
            <p className="mt-5 max-w-xl text-lg font-semibold leading-snug text-navy/80">
              One platform. Complete verification. Smarter decisions.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-navy/60 sm:text-lg">
              Trust is the foundation of every financial transaction. SimVerify helps institutions
              establish digital trust before approving loans, financing assets or completing
              high-value transactions.
            </p>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-navy/55">
              Borrower intelligence, vehicle verification, device verification, SIM behavioural
              analytics and AI-powered Trust Scoring — one view of transaction risk. Right now the
              lens is on {current.title.toLowerCase()}.
            </p>

            <div className="mt-8 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap sm:items-center">
              <a
                href="#demo"
                className="inline-flex h-12 w-full shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-verify-gold px-6 text-sm font-semibold tracking-tight text-navy shadow-[0_12px_28px_-12px_rgba(255,191,24,0.85)] transition hover:bg-[#ffd24d] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verify-gold focus-visible:ring-offset-2 sm:w-auto"
              >
                Request a Live Demo
                <ArrowIcon className="h-3.5 w-3.5" />
              </a>
              <a
                href="#videos"
                className="inline-flex h-12 w-full shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-verify-royal px-6 text-sm font-semibold tracking-tight text-white shadow-[0_12px_28px_-12px_rgba(79,72,236,0.85)] transition hover:bg-[#3d37d4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verify-royal focus-visible:ring-offset-2 sm:w-auto"
              >
                <Play className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
                Watch Product Video
              </a>
            </div>

            <ul className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
              {[
                { href: "#sales", label: "Contact Sales" },
                { href: "#appointment", label: "Book Consultation" },
                { href: "#apis", label: "Developer APIs" },
              ].map((item) => (
                <li key={item.href}>
                  <a
                    href={item.href}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-navy/65 transition hover:text-verify-royal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-verify-gold"
                  >
                    {item.label}
                    <ArrowIcon className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div className="relative mx-auto w-full max-w-md lg:max-w-none" initial="hidden" animate="show" variants={fadeUp}>
            <div className="relative mx-auto aspect-[4/5] w-full max-w-[28rem] lg:max-w-none">
              <div
                className="absolute inset-0 bg-verify-royal/15"
                style={{
                  clipPath: "polygon(50% 0%, 100% 16%, 100% 70%, 50% 100%, 0% 70%, 0% 16%)",
                }}
                aria-hidden="true"
              />
              <div
                className="absolute inset-[10px] overflow-hidden bg-navy"
                style={{
                  clipPath: "polygon(50% 0%, 100% 16%, 100% 70%, 50% 100%, 0% 70%, 0% 16%)",
                }}
              >
                <MediaSlot
                  src={HERO_MEDIA.src}
                  alt={HERO_MEDIA.alt}
                  spec={HERO_MEDIA.spec}
                  className="absolute inset-0 h-full w-full"
                  tone="dark"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/55 via-transparent to-navy/20" />
              </div>
              <span className="absolute left-1/2 top-[7%] z-[1] flex h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full bg-verify-green text-navy shadow-[0_10px_24px_-8px_rgba(74,222,128,0.9)]">
                <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" aria-hidden="true">
                  <path d="M3.5 8.2L6.4 11.1L12.5 4.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </span>
            </div>
            <aside className="relative z-[1] mx-auto mt-6 max-w-md border border-navy/10 bg-white p-5 lg:absolute lg:-bottom-2 lg:-left-6 lg:mt-0 lg:w-[16.5rem]">
              <p className="font-mono text-[11px] tracking-[0.18em] text-verify-royal">Signal in view</p>
              <p className="mt-2 text-base font-semibold tracking-tight text-navy">{current.title}</p>
              <p className="mt-2 text-sm leading-relaxed text-navy/60">{current.line}</p>
            </aside>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default VerifyHero;
